import { useCallback, useEffect, useState } from 'react';
import { useHubContext } from '../../shell/HubContext';
import type { ApiClient } from '../../api/client';
import type { CtResourceLink, CtResourceSectionId, SaveCtResourceRequest } from '../../api/types';
import { DocumentPreviewModal, type DocumentPreviewTarget } from '../../components/DocumentPreviewModal';
import { Modal } from '../../components/Modal';
import { CtResourceDialog } from './CtResourceDialog';

/**
 * A page of Clinical Team reference documents and forms. Two sections use it — CT Resources and
 * CT Interview Materials — each showing the entries stored for it.
 *
 * Everyone who can see the page can also add, edit and delete its entries: an entry is a name, an
 * optional description, a type, and a PDF, a link, or both. The PDF is uploaded to the hub and
 * previewed in a dialog here (Preview PDF); the link opens in a new tab (Open link). A link-only
 * entry simply has no Preview button.
 *
 * Deliberately not folded into the public forms page next door, though both are lists of links. That
 * page hands out URLs anyone on the internet can open and carries a notice saying so; these are
 * internal documents behind the section's Entra group.
 */
export function CtResourcesSection() {
  return (
    <ReferenceDocumentsPage
      sectionId="ct-resources"
      title="CT Resources"
      subtitle="The Clinical Team documents and forms, in one place."
    />
  );
}

/** The interview packet and clinic visit agenda — what an interviewer needs on the day. */
export function CtInterviewMaterialsSection() {
  return (
    <ReferenceDocumentsPage
      sectionId="ct-interview-materials"
      title="CT Interview Materials"
      subtitle="What you need to interview a Clinical Team candidate and run their clinic visit."
    />
  );
}

interface ReferenceDocumentsPageProps {
  sectionId: CtResourceSectionId;
  title: string;
  subtitle: string;
}

/** Which dialog is open: adding, editing one entry, or none. */
type Editing = { mode: 'add' } | { mode: 'edit'; link: CtResourceLink } | null;

function ReferenceDocumentsPage({ sectionId, title, subtitle }: ReferenceDocumentsPageProps) {
  const { api } = useHubContext();

  const [links, setLinks] = useState<CtResourceLink[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<DocumentPreviewTarget | null>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const [deleting, setDeleting] = useState<CtResourceLink | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) =>
      api
        .listCtResourceLinks(sectionId, signal)
        .then((loaded) => {
          setLinks(loaded);
          setLoadError(null);
        })
        .catch((error: unknown) => {
          if (signal?.aborted) return;

          console.error(`Could not load the ${sectionId} links`, error);
          setLoadError('The documents could not be loaded. Reload the page to try again.');
        }),
    [api, sectionId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  async function save(request: SaveCtResourceRequest) {
    if (editing?.mode === 'edit') {
      await api.updateCtResource(sectionId, editing.link.id, request);
    } else {
      await api.createCtResource(sectionId, request);
    }

    // Reloaded rather than patched in place, so the page shows the order and the state the server
    // settled on — including anything somebody else changed in the meantime.
    setEditing(null);
    await load();
  }

  async function confirmDelete() {
    if (!deleting) return;

    setDeleteBusy(true);
    setDeleteError(null);

    try {
      await api.deleteCtResource(sectionId, deleting.id);
      setDeleting(null);
      await load();
    } catch (error: unknown) {
      console.error('Could not delete the entry', error);
      setDeleteError('It could not be deleted. Try again in a moment.');
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <div className="care-page care-page--wide">
      <header className="care-page__header ct-resources__header">
        <div>
          <h1 className="care-page__title">{title}</h1>
          <p className="care-page__subtitle">{subtitle}</p>
        </div>
        {links !== null && (
          <button type="button" className="button button--primary" onClick={() => setEditing({ mode: 'add' })}>
            Add document
          </button>
        )}
      </header>

      {loadError && (
        <p className="page-message page-message--error" role="alert">
          {loadError}
        </p>
      )}

      {links === null && !loadError && <p className="page-message">Loading…</p>}

      {links !== null && links.length === 0 && (
        <p className="page-message">Nothing has been added here yet. Use Add document to add a PDF or a link.</p>
      )}

      {links !== null && links.length > 0 && (
        <div className="ct-resources">
          {links.map((link) => (
            <ResourceCard
              key={link.id}
              link={link}
              onPreview={() => setPreviewing(previewTarget(sectionId, link, api))}
              onEdit={() => setEditing({ mode: 'edit', link })}
              onDelete={() => {
                setDeleteError(null);
                setDeleting(link);
              }}
            />
          ))}
        </div>
      )}

      {previewing && <DocumentPreviewModal target={previewing} onClose={() => setPreviewing(null)} />}

      {editing && (
        <CtResourceDialog
          editing={editing.mode === 'edit' ? editing.link : null}
          onSave={save}
          onClose={() => setEditing(null)}
        />
      )}

      <Modal
        open={deleting !== null}
        title="Delete this document?"
        onClose={() => setDeleting(null)}
        footer={
          <>
            <button type="button" className="button button--quiet" onClick={() => setDeleting(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="button button--danger-solid"
              disabled={deleteBusy}
              onClick={() => void confirmDelete()}
            >
              {deleteBusy ? 'Deleting…' : 'Delete'}
            </button>
          </>
        }
      >
        {deleting && (
          <>
            <p>
              <strong>{deleting.name}</strong> is removed from this page for everyone
              {deleting.hasDocument ? ', and its PDF is deleted' : ''}. This cannot be undone here.
            </p>
            {deleteError && (
              <p className="page-message page-message--error" role="alert">
                {deleteError}
              </p>
            )}
          </>
        )}
      </Modal>
    </div>
  );
}

interface ResourceCardProps {
  link: CtResourceLink;
  onPreview: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function ResourceCard({ link, onPreview, onEdit, onDelete }: ResourceCardProps) {
  // What it opens, said before it is clicked. A Smartsheet form looks nothing like this app.
  const isForm = link.kind === 'form';
  const kindLabel = isForm ? 'Form' : link.kind === 'document' ? 'Document' : link.kind;
  const hasNothing = !link.hasDocument && link.url === null;

  return (
    <section className="ct-resources__card">
      <h2 className="ct-resources__title">{link.name}</h2>
      {link.description && <p className="ct-resources__description">{link.description}</p>}

      {/* Only an entry carried over from before can be in this state — the form does not allow it.
          Shown rather than hidden, so somebody can fill it in or delete it. */}
      {hasNothing && (
        <p className="ct-resources__empty">Nothing attached yet. Edit it to add a PDF or a link.</p>
      )}

      <div className="ct-resources__actions">
        {link.hasDocument && (
          <button type="button" className="button button--primary" onClick={onPreview}>
            Preview PDF
          </button>
        )}
        {link.url !== null && (
          // New tab, with `noreferrer`: these go to another host entirely, and somebody who follows
          // one from here should still have the hub where they left it. Quiet beside Preview, so a
          // card with both has one obvious first choice.
          <a
            className={`button ${link.hasDocument ? 'button--quiet' : 'button--primary'}`}
            href={link.url}
            target="_blank"
            rel="noreferrer"
          >
            {isForm ? 'Open' : 'Open link'}
          </a>
        )}
        <span className="ct-resources__kind">{kindLabel}</span>
      </div>

      <div className="ct-resources__manage">
        <button type="button" className="ct-resources__manage-button" onClick={onEdit}>
          Edit
        </button>
        <button
          type="button"
          className="ct-resources__manage-button ct-resources__manage-button--danger"
          onClick={onDelete}
        >
          Delete
        </button>
      </div>
    </section>
  );
}

/** Built once per click and held in state, so the modal's `load` stays the same function while it is open. */
function previewTarget(
  sectionId: CtResourceSectionId,
  link: CtResourceLink,
  api: ApiClient,
): DocumentPreviewTarget {
  const fileName = `${link.name}.pdf`;

  return {
    title: link.name,
    fileName,
    load: (signal) => api.downloadCtResourceDocument(sectionId, link.id, signal),
  };
}
